import { chatDocumentSchema } from '@/apis/privateChatDocuments/message';
import { voucherOfferChatCardSchema } from '@/apis/chatCards/contract';
import { checklistChatCardSchema } from '@/apis/chatCards/contract';
import { aftercareChatCardSchema } from '@/apis/chatCards/contract';
import { policyChatCardSchema } from '@/apis/chatCards/contract';
import { priceChatCardSchema } from '@/apis/chatCards/contract';
import { bookingChatCardSchema } from '@/apis/chatCards/contract';
import {
  paymentLinkChatCardSchema,
  membershipPaymentLinkChatCardSchema,
} from '@/apis/chatCards/contract';
import { formRequestChatCardSchema } from '@/apis/chatCards/contract';
import { planProposalChatCardSchema } from '@/apis/chatCards/contract';
import { chatImageSchema } from '@/apis/privateChatImages/contract';
import axios, { CanceledError } from 'axios';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import {
  AppError,
  glowRequest,
  type LocalSession,
  type RequestParams,
} from '@/networks';
import { requireCurrentSession } from '@/networks/session';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePreferenceStore } from '@/stores/usePreferenceStore';

export const chatUuid = z.uuid().regex(/^[0-9a-f-]{36}$/);
const time = z.iso.datetime({ offset: true });
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const version = z.number().int().min(0).max(2147483647);
export const actualRoomSchema = z.strictObject({
  id: chatUuid,
  roomType: z.enum(['HOST', 'CLINIC', 'AI', 'SUPPORT']),
  status: z.enum([
    'OPEN',
    'WAITING_HOST',
    'WAITING_MEMBER',
    'CLOSED',
    'ARCHIVED',
  ]),
  subjectCode: z.string().regex(/^[A-Z][A-Z0-9_]{0,79}$/),
  journeyId: chatUuid.nullable(),
  hostAssigned: z.boolean(),
  version,
  createdAt: time,
  updatedAt: time,
  firstResponseDueAt: time.nullable(),
  firstRespondedAt: time.nullable(),
  unreadCount: count,
  lastReadMessageId: chatUuid.nullable(),
  lastReadAt: time.nullable(),
});
export const actualMessageSchema = z
  .strictObject({
    id: chatUuid,
    clientMessageId: chatUuid.optional(),
    senderType: z.enum(['SYSTEM', 'MEMBER', 'HOST', 'ADMIN']),
    messageType: z.enum(['SYSTEM', 'TEXT', 'IMAGE', 'FILE', 'CARD']),
    originalLocale: z.enum(['en', 'zh-CN', 'ko']),
    text: z.string().min(1).max(2000).optional(),
    image: chatImageSchema.optional(),
    document: chatDocumentSchema.optional(),
    card: z
      .union([
        planProposalChatCardSchema,
        formRequestChatCardSchema,
        paymentLinkChatCardSchema,
        membershipPaymentLinkChatCardSchema,
        bookingChatCardSchema,
        priceChatCardSchema,
        policyChatCardSchema,
        aftercareChatCardSchema,
        checklistChatCardSchema,
        voucherOfferChatCardSchema,
      ])
      .optional(),
    systemCode: z.literal('WAITING_FOR_HOST').optional(),
    estimatedResponseAt: time.optional(),
    estimateSource: z.enum(['UNKNOWN', 'NEXT_SHIFT_MEDIAN']).optional(),
    createdAt: time,
    readByMeAt: time.optional(),
    readByMemberAt: time.optional(),
    readByStaffAt: time.optional(),
  })
  .refine((m) => {
    if (m.document !== undefined && m.messageType !== 'FILE') return false;
    if (m.messageType === 'FILE')
      return (
        m.senderType !== 'SYSTEM' &&
        !!m.clientMessageId &&
        !!m.document &&
        m.text === undefined &&
        m.image === undefined &&
        m.card === undefined &&
        m.systemCode === undefined &&
        m.estimateSource === undefined &&
        m.estimatedResponseAt === undefined
      );

    if (
      m.card?.type === 'PAYMENT_LINK' ||
      m.card?.type === 'BOOKING' ||
      m.card?.type === 'PRICE_GUARANTEE' ||
      m.card?.type === 'POLICY_SUMMARY' ||
      m.card?.type === 'AFTERCARE_CHECKIN' ||
      m.card?.type === 'CHECKLIST' ||
      m.card?.type === 'VOUCHER_OFFER'
    )
      return (
        m.messageType === 'TEXT' &&
        m.senderType === 'HOST' &&
        !!m.clientMessageId &&
        !!m.text &&
        m.image === undefined &&
        m.systemCode === undefined &&
        m.estimateSource === undefined &&
        m.estimatedResponseAt === undefined
      );
    if (m.card?.type === 'FORM_REQUEST')
      return (
        m.messageType === 'TEXT' &&
        ['HOST', 'ADMIN'].includes(m.senderType) &&
        !!m.clientMessageId &&
        m.id === m.card.requestId &&
        !!m.text &&
        m.image === undefined &&
        m.systemCode === undefined &&
        m.estimateSource === undefined &&
        m.estimatedResponseAt === undefined
      );
    if (m.messageType === 'CARD')
      return (
        ['HOST', 'ADMIN'].includes(m.senderType) &&
        m.card?.type === 'PLAN_PROPOSAL' &&
        m.clientMessageId === m.card.proposalId &&
        m.text === undefined &&
        m.image === undefined &&
        m.systemCode === undefined &&
        m.estimateSource === undefined &&
        m.estimatedResponseAt === undefined
      );
    if (m.card !== undefined) return false;
    if (m.messageType === 'IMAGE')
      return (
        m.senderType !== 'SYSTEM' &&
        !!m.clientMessageId &&
        !!m.image &&
        m.text === undefined &&
        m.systemCode === undefined &&
        m.estimateSource === undefined &&
        m.estimatedResponseAt === undefined
      );
    if (m.image !== undefined) return false;
    return m.messageType === 'TEXT'
      ? m.senderType !== 'SYSTEM' &&
          !!m.text &&
          !m.systemCode &&
          !m.estimateSource &&
          !m.estimatedResponseAt
      : m.senderType === 'SYSTEM' &&
          m.systemCode === 'WAITING_FOR_HOST' &&
          m.text === undefined;
  });
export const actualMessagePageSchema = z
  .strictObject({
    count,
    rows: z.array(actualMessageSchema).max(50),
    nextCursor: chatUuid.optional(),
    isLast: z.boolean(),
  })
  .refine(
    (p) =>
      p.count === p.rows.length &&
      new Set(p.rows.map((m) => m.id)).size === p.rows.length &&
      (p.isLast ? !p.nextCursor : !!p.nextCursor),
  );
export const actualRoomPageSchema = z
  .strictObject({
    count,
    rows: z.array(actualRoomSchema).max(50),
    nextPage: z.number().int().min(2).max(10000).nullable().optional(),
    isLast: z.boolean(),
  })
  .refine(
    (p) =>
      p.count >= p.rows.length &&
      new Set(p.rows.map((r) => r.id)).size === p.rows.length &&
      (p.isLast ? !p.nextPage : !!p.nextPage),
  );
export const actualReadSchema = z.strictObject({
  readAt: time,
  unreadCount: count,
  markedCount: count,
  lastReadMessageId: chatUuid.optional(),
});
export type ActualChatRoom = z.infer<typeof actualRoomSchema>;
export type ActualChatMessage = z.infer<typeof actualMessageSchema>;
export type ActualMessagePage = z.infer<typeof actualMessagePageSchema>;
export interface ActualSendBody {
  clientMessageId: string;
  roomVersion: number;
  text: string;
}

/** Do not retain Axios configs, request/response bodies, raw exceptions or translated payloads. */
export function safeChatError(error: unknown): AppError {
  if (axios.isCancel(error))
    return new AppError(new CanceledError('CHAT_CANCELED'));
  if (!axios.isAxiosError(error))
    return new AppError(new Error('CHAT_RESPONSE_INVALID'));
  const status = error.response?.status;
  if (!status)
    return new AppError(
      new axios.AxiosError('CHAT_REQUEST_FAILED', error.code),
    );
  const code = axios.isAxiosError(error)
    ? error.response?.data?.message
    : undefined;
  const key =
    typeof code === 'string' && /^[A-Z][A-Z0-9_]{0,99}$/.test(code)
      ? code
      : 'CHAT_REQUEST_FAILED';
  return new AppError({
    isAxiosError: true,
    code: axios.isAxiosError(error) ? error.code : undefined,
    ...(status
      ? {
          response: {
            status,
            data: { statusCode: status, message: key },
            headers: {},
          },
        }
      : {}),
  });
}
async function request<T>(
  binding: LocalSession,
  schema: z.ZodType<T>,
  params: RequestParams,
): Promise<T> {
  requireCurrentSession(binding);
  try {
    const response = await glowRequest<unknown>({
      ...params,
      localSession: binding,
      skipAuthRetry: params.method !== 'get',
    });
    requireCurrentSession(binding);
    const parsed = schema.safeParse(response.data);
    if (!parsed.success) throw new Error('CHAT_RESPONSE_INVALID');
    return parsed.data;
  } catch (error) {
    throw safeChatError(error);
  }
}
function roomUrl(id: string) {
  if (!chatUuid.safeParse(id).success)
    throw new AppError(new Error('CHAT_ROOM_INVALID'));
  return `/app/v1/chat/rooms/${id}`;
}
export const requestActualRooms = (
  binding: LocalSession,
  pageNum: number,
  signal?: AbortSignal,
) =>
  request(binding, actualRoomPageSchema, {
    method: 'get',
    url: '/app/v1/chat/rooms',
    queryParams: { pageNum, pageSize: 20 },
    signal,
  });
export const requestActualRoom = async (
  binding: LocalSession,
  id: string,
  signal?: AbortSignal,
) => {
  const room = (
    await request(binding, z.strictObject({ row: actualRoomSchema }), {
      method: 'get',
      url: roomUrl(id),
      signal,
    })
  ).row;
  if (room.id !== id) throw safeChatError(new Error('CHAT_RESPONSE_INVALID'));
  return room;
};
export const requestActualMessages = (
  binding: LocalSession,
  id: string,
  cursor?: string,
  signal?: AbortSignal,
) =>
  request(binding, actualMessagePageSchema, {
    method: 'get',
    url: `${roomUrl(id)}/messages`,
    queryParams: { limit: 20, ...(cursor ? { cursor } : {}) },
    signal,
  });
export const requestActualSend = async (
  binding: LocalSession,
  id: string,
  body: ActualSendBody,
  signal?: AbortSignal,
) => {
  const message = (
    await request(binding, z.strictObject({ row: actualMessageSchema }), {
      method: 'post',
      url: `${roomUrl(id)}/messages`,
      requestBody: body,
      signal,
    })
  ).row;
  if (
    message.clientMessageId !== body.clientMessageId ||
    message.senderType !== 'MEMBER' ||
    message.messageType !== 'TEXT' ||
    message.text !== body.text
  )
    throw safeChatError(new Error('CHAT_RESPONSE_INVALID'));
  return message;
};
export const requestActualRead = async (
  binding: LocalSession,
  id: string,
  lastMessageId: string,
  signal?: AbortSignal,
) =>
  (
    await request(binding, z.strictObject({ row: actualReadSchema }), {
      method: 'post',
      url: `${roomUrl(id)}/read`,
      requestBody: { lastMessageId },
      signal,
    })
  ).row;

/** Metadata only. Sensitive message bodies and write variables never enter Query/Mutation caches. */
export function useActualRooms(pageNum: number) {
  const actorId = useAuthStore((s) => s.member?.id),
    epoch = useAuthStore((s) => s.sessionEpoch),
    authenticated = useAuthStore((s) => !!s.accessToken),
    display = usePreferenceStore((s) => `${s.locale ?? 'en'}:${s.currency}`);
  return useQuery({
    queryKey: ['actualChatRooms', actorId, epoch, display, pageNum],
    enabled: !!actorId && authenticated,
    queryFn: ({ signal }) =>
      requestActualRooms({ actorId: actorId!, epoch }, pageNum, signal),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: 30000,
  });
}
